import React from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { user } from "__shared/auth";
import { useAuth0 } from "@auth0/auth0-react";

// This component wraps elements passed to AuthorizedRoute to:
// 1. Check authentication
// 2. Provide route props for backward compatibility with v5 components
const AuthorizedRouteWrapper = ({ element, render }) => {
  const { isAuthenticated: isAuthenticatedAuth0, loginWithRedirect } =
    useAuth0();

  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // If not authenticated, trigger login
  if (!isAuthenticatedAuth0) {
    loginWithRedirect();
    return null;
  }

  // Create props object similar to what React Router v5 used to provide
  const routeProps = {
    match: {
      params,
      isExact: true,
      path: location.pathname,
      url: location.pathname,
    },
    location,
    history: {
      push: (path, state) => navigate(path, { state }),
      replace: (path, state) => navigate(path, { state, replace: true }),
      go: (n) => navigate(n),
      goBack: () => navigate(-1),
      goForward: () => navigate(1),
    },
  };

  // If render function provided (v5 pattern), call it with route props
  if (render) {
    return render({ ...routeProps, user });
  }

  // If element provided (v6 pattern), wrap it to provide route props as context
  if (element) {
    // Clone element to pass route props
    if (React.isValidElement(element)) {
      return React.cloneElement(element, { ...routeProps, user });
    }
    return element;
  }

  return null;
};

// For v6 usage, this should be called in a lazy wrapper
const AuthorizedRoute = ({ element, render }) => {
  return <AuthorizedRouteWrapper element={element} render={render} />;
};

export default AuthorizedRoute;
export { AuthorizedRouteWrapper };
